import { supabase } from './supabase'
import { toast } from 'sonner'

// Tipos para integração PQC com registros médicos
export interface PQCEncryptedData {
  encrypted_content: string
  kyber_public_key: string
  dilithium_signature: string
  encryption_metadata: {
    algorithm: 'kyber' | 'dilithium'
    key_size: number
    timestamp: string
    version: string
    key_owner?: 'patient' | 'doctor'
  }
}

export interface MedicalRecordPQC {
  record_id: string
  patient_id: string
  doctor_id: string
  encrypted_data: PQCEncryptedData
  access_permissions: string[]
  encryption_key_id: string
  is_shared: boolean
  shared_with: string[]
  audit_trail: PQCAuditEntry[]
}

export interface PQCAuditEntry {
  action: 'encrypt' | 'decrypt' | 'share' | 'revoke' | 'access'
  user_id: string
  timestamp: string
  ip_address?: string
  user_agent?: string
  success: boolean
  error_message?: string
}

export interface PQCKeyPair {
  id: string
  public_key: string
  private_key: string
  algorithm: 'kyber' | 'dilithium'
  created_at: string
  expires_at?: string
}

// Classe principal para integração PQC com registros médicos
export class MedicalPQCIntegration {
  private static instance: MedicalPQCIntegration
  private keyCache: Map<string, PQCKeyPair> = new Map()
  private encryptionQueue: Map<string, Promise<any>> = new Map()

  static getInstance(): MedicalPQCIntegration {
    if (!this.instance) {
      this.instance = new MedicalPQCIntegration()
    }
    return this.instance
  }

  // Gerar par de chaves PQC para um usuário
  async generateUserKeyPair(userId: string, algorithm: 'kyber' | 'dilithium' = 'kyber'): Promise<PQCKeyPair | null> {
    try {
      console.log('🔑 Iniciando geração de chave PQC para usuário:', userId)
      
      // Verificar se o usuário autenticado tem permissão para criar chaves para este usuário
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        console.error('❌ Usuário não autenticado')
        return null
      }
      
      console.log('👤 Usuário autenticado:', user.id, 'criando chave para:', userId)
      
      // Verificar se já existe uma chave ativa para este usuário
      const { data: existingKey } = await supabase
        .from('pqc_keys')
        .select('*')
        .eq('user_id', userId)
        .eq('key_type', algorithm)
        .eq('is_active', true)
        .single()
      
      if (existingKey) {
        console.log('✅ Chave PQC já existe para o usuário:', existingKey.id)
        const keyPair: PQCKeyPair = {
          id: existingKey.id,
          public_key: existingKey.public_key,
          private_key: existingKey.private_key_encrypted,
          algorithm: existingKey.algorithm,
          created_at: existingKey.created_at,
          expires_at: existingKey.expires_at
        }
        this.keyCache.set(keyPair.id, keyPair)
        return keyPair
      }
      
      // Simular geração de chaves PQC (em produção, usar biblioteca real)
      const keyId = `${algorithm}_${userId}_${Date.now()}`
      const publicKey = this.generateMockKey('public', algorithm)
      const privateKey = this.generateMockKey('private', algorithm)
      const expiresAt = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      
      console.log('🔑 Chaves geradas, tentando salvar no banco...')
      
      // Preparar dados para inserção - key_purpose baseado no algoritmo
      const keyPurpose = algorithm === 'kyber' ? 'encryption' : 'signing'
      const insertData = {
        user_id: userId,
        key_type: algorithm,
        algorithm,
        public_key: publicKey,
        private_key_encrypted: privateKey,
        key_purpose: keyPurpose,
        is_active: true,
        expires_at: expiresAt
      }
      
      console.log('🔑 Dados para inserção (campos essenciais):', { ...insertData, private_key_encrypted: '[REDACTED]' })

      // Tentar inserção com campos essenciais primeiro
      let data, error
      
      try {
        const result = await supabase
          .from('pqc_keys')
          .insert(insertData)
          .select()
          .single()
        
        data = result.data
        error = result.error
      } catch (insertError) {
        console.error('❌ Erro na inserção inicial:', insertError)
        
        // Fallback: tentar com campos mínimos mas incluindo key_purpose
        const keyPurpose = algorithm === 'kyber' ? 'encryption' : 'signing'
        const minimalData = {
          user_id: userId,
          key_type: algorithm,
          algorithm,
          public_key: publicKey,
          private_key_encrypted: privateKey,
          key_purpose: keyPurpose
        }
        
        console.log('🔄 Tentando inserção mínima:', { ...minimalData, private_key_encrypted: '[REDACTED]' })
        
        const fallbackResult = await supabase
          .from('pqc_keys')
          .insert(minimalData)
          .select()
          .single()
        
        data = fallbackResult.data
        error = fallbackResult.error
      }

      if (error) {
        console.error('❌ Erro ao salvar chave PQC:', error)
        console.error('📋 Detalhes do erro:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        })
        
        // Verificar se é erro de RLS
        if (error.code === '42501') {
          console.error('🚫 Erro de política RLS - usuário não tem permissão para criar chave para este paciente')
          console.error('👤 Usuário autenticado:', user.id)
          console.error('🎯 Tentando criar chave para:', userId)
          
          // Verificar permissões do usuário
          const { data: permissions } = await supabase
            .from('access_permissions')
            .select('*')
            .eq('patient_id', userId)
            .eq('requester_id', user.id)
            .eq('status', 'approved')
          
          console.error('🔍 Permissões encontradas:', permissions)
        }
        
        return null
      }

      console.log('✅ Chave PQC salva com sucesso:', data)
      
      // Criar objeto de retorno
      const keyPair: PQCKeyPair = {
        id: data.id,
        public_key: publicKey,
        private_key: privateKey,
        algorithm,
        created_at: data.created_at || new Date().toISOString(),
        expires_at: data.expires_at || expiresAt
      }

      // Cache da chave
      this.keyCache.set(keyPair.id, keyPair)

      return keyPair
    } catch (error) {
      console.error('💥 Erro crítico ao gerar par de chaves PQC:', error)
      return null
    }
  }

  // Criptografar dados de registro médico
  async encryptMedicalRecord(
    recordData: any,
    patientId: string,
    doctorId: string
  ): Promise<{ encrypted: PQCEncryptedData; keyId: string } | null> {
    try {
      console.log('🔐 Iniciando criptografia de registro médico')
      console.log('👤 Paciente ID:', patientId)
      console.log('👨‍⚕️ Médico ID:', doctorId)
      
      // Primeiro, tentar obter chave Kyber do paciente
      let keyPair = await this.getUserKeyPair(patientId, 'kyber')
      let keyOwner: 'patient' | 'doctor' = 'patient'
      
      if (!keyPair) {
        console.log('⚠️ Paciente não possui chave Kyber, tentando usar chave do médico...')
        // Fallback: usar chave do médico para criptografia
        keyPair = await this.getUserKeyPair(doctorId, 'kyber')
        keyOwner = 'doctor'
        
        if (!keyPair) {
          console.log('❌ Nem paciente nem médico possuem chaves Kyber')
          throw new Error('Nenhuma chave de criptografia disponível para paciente ou médico')
        }
        
        console.log('✅ Usando chave Kyber do médico para criptografia')
      } else {
        console.log('✅ Usando chave Kyber do paciente para criptografia')
      }

      console.log('🔑 Chave selecionada:', {
        id: keyPair.id,
        algorithm: keyPair.algorithm,
        owner: keyOwner
      })

      // Serializar dados do registro
      const dataString = JSON.stringify(recordData)
      
      // Simular criptografia PQC (em produção, usar biblioteca real)
      const encryptedContent = this.mockEncrypt(dataString, keyPair.public_key)
      
      // Para assinatura, tentar usar chave Dilithium do médico
      let signature = ''
      const doctorDilithiumKey = await this.getUserKeyPair(doctorId, 'dilithium')
      if (doctorDilithiumKey) {
        signature = this.mockSign(dataString, doctorDilithiumKey.private_key)
        console.log('✅ Assinatura criada com chave Dilithium do médico')
      } else {
        console.log('⚠️ Médico não possui chave Dilithium, usando assinatura simulada')
        signature = this.mockSign(dataString, 'simulated_private_key')
      }

      const encryptedData: PQCEncryptedData = {
        encrypted_content: encryptedContent,
        kyber_public_key: keyPair.public_key,
        dilithium_signature: signature,
        encryption_metadata: {
          algorithm: keyPair.algorithm,
          key_size: keyPair.public_key.length,
          timestamp: new Date().toISOString(),
          version: '1.0',
          key_owner: keyOwner // Adicionar informação sobre quem é o dono da chave
        }
      }

      console.log('🎉 Criptografia concluída com sucesso')

      // Log de auditoria
      await this.createAuditEntry({
        action: 'encrypt',
        user_id: doctorId,
        timestamp: new Date().toISOString(),
        success: true
      }, patientId)

      return {
        encrypted: encryptedData,
        keyId: keyPair.id
      }
    } catch (error) {
      console.error('💥 Erro ao criptografar registro médico:', error)
      
      // Log de auditoria para falha
      await this.createAuditEntry({
        action: 'encrypt',
        user_id: doctorId,
        timestamp: new Date().toISOString(),
        success: false,
        error_message: error instanceof Error ? error.message : 'Erro desconhecido'
      }, patientId)
      
      return null
    }
  }

  // Descriptografar dados de registro médico
  async decryptMedicalRecord(
    encryptedData: PQCEncryptedData,
    userId: string,
    patientId: string,
    recordId?: string
  ): Promise<any | null> {
    try {
      console.log('🔓 Iniciando descriptografia de registro médico')
      console.log('👤 Usuário solicitante:', userId)
      console.log('🏥 Paciente:', patientId)
      console.log('📄 Record ID:', recordId)
      
      // Verificar permissões de acesso
      const hasAccess = await this.checkAccessPermission(userId, patientId, recordId)
      if (!hasAccess) {
        throw new Error('Acesso negado: usuário não tem permissão para descriptografar este registro')
      }

      console.log('✅ Permissão de acesso verificada')

      // Determinar qual chave usar baseado nos metadados
      const keyOwner = encryptedData.encryption_metadata?.key_owner || 'patient'
      console.log('🔑 Proprietário da chave de criptografia:', keyOwner)
      
      let keyPair: PQCKeyPair | null = null
      
      if (keyOwner === 'patient') {
        // Tentar obter chave do paciente primeiro
        keyPair = await this.getUserKeyPair(patientId, 'kyber')
        if (!keyPair) {
          console.log('⚠️ Chave do paciente não encontrada, tentando chave do usuário solicitante...')
          keyPair = await this.getUserKeyPair(userId, 'kyber')
        }
      } else {
        // Se foi criptografado com chave do médico, tentar obter a chave do médico
        // Primeiro tentar do usuário solicitante, depois do paciente
        keyPair = await this.getUserKeyPair(userId, 'kyber')
        if (!keyPair) {
          console.log('⚠️ Chave do médico não encontrada, tentando chave do paciente...')
          keyPair = await this.getUserKeyPair(patientId, 'kyber')
        }
      }
      
      if (!keyPair) {
        throw new Error('Chave de descriptografia não encontrada para nenhum dos usuários')
      }

      console.log('✅ Chave de descriptografia encontrada:', {
        id: keyPair.id,
        algorithm: keyPair.algorithm
      })

      // Verificar assinatura (se disponível)
      const signatureValid = this.mockVerifySignature(
        encryptedData.encrypted_content,
        encryptedData.dilithium_signature,
        encryptedData.kyber_public_key
      )

      if (!signatureValid) {
        console.log('⚠️ Assinatura digital inválida - dados podem ter sido alterados')
        // Não bloquear a descriptografia por enquanto, apenas avisar
      } else {
        console.log('✅ Assinatura digital verificada')
      }

      // Descriptografar conteúdo
      const decryptedString = this.mockDecrypt(
        encryptedData.encrypted_content,
        keyPair.private_key
      )

      const decryptedData = JSON.parse(decryptedString)
      
      console.log('🎉 Descriptografia concluída com sucesso')

      // Log de auditoria
      await this.createAuditEntry({
        action: 'decrypt',
        user_id: userId,
        timestamp: new Date().toISOString(),
        success: true
      }, patientId)

      return decryptedData
    } catch (error) {
      console.error('💥 Erro ao descriptografar registro médico:', error)
      
      // Log de auditoria para falha
      await this.createAuditEntry({
        action: 'decrypt',
        user_id: userId,
        timestamp: new Date().toISOString(),
        success: false,
        error_message: error instanceof Error ? error.message : 'Erro desconhecido'
      }, patientId)
      
      return null
    }
  }

  // Compartilhar registro médico com outro usuário
  async shareRecordWithUser(
    recordId: string,
    fromUserId: string,
    toUserId: string,
    permissions: string[] = ['read']
  ): Promise<boolean> {
    try {
      // Verificar se o usuário tem permissão para compartilhar
      const canShare = await this.checkSharePermission(fromUserId, recordId)
      if (!canShare) {
        throw new Error('Usuário não tem permissão para compartilhar este registro')
      }

      // Criar entrada de compartilhamento
      const { error } = await supabase
        .from('access_permissions')
        .insert({
          requester_id: toUserId,
          patient_id: fromUserId, // Assumindo que fromUserId é o paciente
          record_id: recordId,
          permission_type: permissions.join(','),
          status: 'approved',
          granted_by: fromUserId,
          granted_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 dias
        })

      if (error) {
        throw error
      }

      // Log de auditoria
      await this.createAuditEntry({
        action: 'share',
        user_id: fromUserId,
        timestamp: new Date().toISOString(),
        success: true
      }, fromUserId)

      return true
    } catch (error) {
      console.error('Erro ao compartilhar registro:', error)
      
      await this.createAuditEntry({
        action: 'share',
        user_id: fromUserId,
        timestamp: new Date().toISOString(),
        success: false,
        error_message: error instanceof Error ? error.message : 'Erro desconhecido'
      }, fromUserId)
      
      return false
    }
  }

  // Revogar acesso a um registro
  async revokeRecordAccess(
    recordId: string,
    ownerId: string,
    targetUserId: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('access_permissions')
        .update({ 
          status: 'revoked',
          revoked_at: new Date().toISOString(),
          revoked_by: ownerId
        })
        .eq('record_id', recordId)
        .eq('requester_id', targetUserId)

      if (error) {
        throw error
      }

      // Log de auditoria
      await this.createAuditEntry({
        action: 'revoke',
        user_id: ownerId,
        timestamp: new Date().toISOString(),
        success: true
      }, ownerId)

      return true
    } catch (error) {
      console.error('Erro ao revogar acesso:', error)
      return false
    }
  }

  // Verificar permissão de acesso
  private async checkAccessPermission(userId: string, patientId: string, recordId?: string): Promise<boolean> {
    try {
      // Paciente sempre tem acesso aos próprios dados
      if (userId === patientId) {
        return true
      }

      // Se temos o recordId, verificar se o usuário é o médico que criou o registro
      if (recordId) {
        const { data: recordData, error: recordError } = await supabase
          .from('medical_records')
          .select('doctor_id')
          .eq('id', recordId)
          .single()

        if (!recordError && recordData && recordData.doctor_id === userId) {
          console.log('✅ Usuário é o médico que criou o registro')
          return true
        }
      }

      // Verificar permissões explícitas
      const { data, error } = await supabase
        .from('access_permissions')
        .select('*')
        .eq('patient_id', patientId)
        .eq('requester_id', userId)
        .eq('status', 'approved')
        .gte('expires_at', new Date().toISOString())

      if (error) {
        console.error('Erro ao verificar permissões:', error)
        return false
      }

      return data && data.length > 0
    } catch (error) {
      console.error('Erro ao verificar permissão de acesso:', error)
      return false
    }
  }

  // Verificar permissão de compartilhamento
  private async checkSharePermission(userId: string, recordId: string): Promise<boolean> {
    try {
      // Verificar se o usuário é o dono do registro
      const { data, error } = await supabase
        .from('medical_records')
        .select('patient_id, doctor_id')
        .eq('id', recordId)
        .single()

      if (error || !data) {
        return false
      }

      // Paciente ou médico que criou o registro pode compartilhar
      return userId === data.patient_id || userId === data.doctor_id
    } catch (error) {
      console.error('Erro ao verificar permissão de compartilhamento:', error)
      return false
    }
  }

  // Obter par de chaves do usuário (especificamente Kyber para criptografia)
  private async getUserKeyPair(userId: string, algorithm: 'kyber' | 'dilithium' = 'kyber'): Promise<PQCKeyPair | null> {
    try {
      console.log('🔍 Buscando chave PQC para usuário:', userId, 'algoritmo:', algorithm)
      
      // Verificar cache primeiro
      const cachedKey = Array.from(this.keyCache.values())
        .find(key => key.id.includes(userId) && key.algorithm === algorithm)
      
      if (cachedKey) {
        console.log('✅ Chave encontrada no cache:', cachedKey.id, 'algoritmo:', cachedKey.algorithm)
        return cachedKey
      }

      console.log('🔍 Chave não encontrada no cache, buscando no banco...')
      
      // Buscar no banco especificamente por tipo de chave
      const { data, error } = await supabase
        .from('pqc_keys')
        .select('*')
        .eq('user_id', userId)
        .eq('key_type', algorithm)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)

      if (error) {
        console.error('❌ Erro ao buscar chave no banco:', error)
        return null
      }

      if (!data || data.length === 0) {
        console.log('❌ Nenhuma chave', algorithm, 'encontrada no banco para usuário:', userId)
        return null
      }

      const keyData = data[0]
      console.log('✅ Chave encontrada no banco:', keyData.id, 'algoritmo:', keyData.algorithm)
      
      const keyPair: PQCKeyPair = {
        public_key: keyData.public_key,
        private_key: keyData.private_key_encrypted || '', // Usar campo correto
        id: keyData.id,
        algorithm: keyData.algorithm,
        created_at: keyData.created_at,
        expires_at: keyData.expires_at
      }

      // Cache da chave
      this.keyCache.set(keyPair.id, keyPair)

      return keyPair
    } catch (error) {
      console.error('💥 Erro ao obter par de chaves:', error)
      return null
    }
  }

  // Criar entrada de auditoria
  private async createAuditEntry(entry: PQCAuditEntry, patientId: string): Promise<void> {
    try {
      await supabase
        .from('blockchain_audit')
        .insert({
          entity_type: 'medical_record_pqc',
          entity_id: patientId,
          action_type: entry.action,
          user_id: entry.user_id,
          blockchain_network: 'ethereum',
          transaction_hash: `0x${Math.random().toString(16).substr(2, 64)}`,
          event_data: {
            success: entry.success,
            error_message: entry.error_message,
            timestamp: entry.timestamp
          }
        })
    } catch (error) {
      console.error('Erro ao criar entrada de auditoria:', error)
    }
  }

  // Métodos de simulação PQC (em produção, usar bibliotecas reais)
  private generateMockKey(type: 'public' | 'private', algorithm: string): string {
    const prefix = `${algorithm}_${type}_`
    const randomPart = Math.random().toString(36).substring(2, 15) + 
                      Math.random().toString(36).substring(2, 15)
    return prefix + randomPart
  }

  private mockEncrypt(data: string, publicKey: string): string {
    // Simulação de criptografia - em produção usar biblioteca PQC real
    const encoded = btoa(data)
    return `encrypted_${publicKey.slice(-8)}_${encoded}`
  }

  private mockDecrypt(encryptedData: string, privateKey: string): string {
    // Simulação de descriptografia - em produção usar biblioteca PQC real
    const parts = encryptedData.split('_')
    if (parts.length >= 3) {
      const encoded = parts.slice(2).join('_')
      return atob(encoded)
    }
    throw new Error('Dados criptografados inválidos')
  }

  private mockSign(data: string, privateKey: string): string {
    // Simulação de assinatura digital - em produção usar biblioteca PQC real
    const hash = btoa(data + privateKey).slice(0, 32)
    return `signature_${hash}`
  }

  private mockVerifySignature(data: string, signature: string, publicKey: string): boolean {
    // Simulação de verificação de assinatura - em produção usar biblioteca PQC real
    try {
      const expectedHash = btoa(data + publicKey.replace('public', 'private')).slice(0, 32)
      return signature === `signature_${expectedHash}`
    } catch {
      return false
    }
  }

  // Limpar cache de chaves
  clearKeyCache(): void {
    this.keyCache.clear()
  }

  // Obter estatísticas de segurança
  async getSecurityStatistics(userId: string): Promise<any> {
    try {
      const { data: auditData, error } = await supabase
        .from('blockchain_audit')
        .select('action_type, event_data, created_at')
        .eq('user_id', userId)
        .eq('entity_type', 'medical_record_pqc')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) {
        throw error
      }

      const stats = {
        totalOperations: auditData?.length || 0,
        successfulOperations: auditData?.filter(entry => 
          entry.event_data?.success === true
        ).length || 0,
        failedOperations: auditData?.filter(entry => 
          entry.event_data?.success === false
        ).length || 0,
        operationsByType: {} as Record<string, number>,
        lastActivity: auditData?.[0]?.created_at || null
      }

      // Contar operações por tipo
      auditData?.forEach(entry => {
        const type = entry.action_type
        stats.operationsByType[type] = (stats.operationsByType[type] || 0) + 1
      })

      return stats
    } catch (error) {
      console.error('Erro ao obter estatísticas de segurança:', error)
      return null
    }
  }
}

// Exportar instância singleton
export const medicalPQC = MedicalPQCIntegration.getInstance()
export default medicalPQC